import { AnchorHTMLAttributes, ButtonHTMLAttributes, cloneElement, FunctionComponentElement } from 'react';
import { joinClassNames } from '../../utils';
import { BaseIconProps } from '../Graphics/Icons/BaseIcon';
import { IconProps } from '../Graphics';
import { IconButtonSize, IconButtonVariant } from './enums';
import styles from './styles.module.scss';

export type IconButtonProps = Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> &
  AnchorHTMLAttributes<HTMLAnchorElement> & {
    variant?: IconButtonVariant;
    size?: IconButtonSize;
    title: string;
    href?: string;
    className?: string;
    disabled?: boolean;
    icon: FunctionComponentElement<BaseIconProps | IconProps>;
  };

const hasIconSizeProp = (icon: unknown): icon is IconProps => (icon as IconProps).size !== undefined;

export const IconButton = ({
  variant = IconButtonVariant.TransparentDark,
  size = IconButtonSize.Medium,
  disabled,
  title,
  icon,
  href,
  className,
  ...props
}: IconButtonProps) => {
  const buttonClassNames = joinClassNames(
    styles.button,
    styles[variant],
    styles[size],
    disabled && styles.disabled,
    className
  );

  const iconButtonClasses = hasIconSizeProp(icon.props)
    ? [styles.icon]
    : [styles.icon, styles.iconDefaultSize];
  const iconClassNames = joinClassNames(...iconButtonClasses, icon.props.className);

  // <button> doesn't take href attributes, so we render <a> when href is provided.
  return href ? (
    <a className={buttonClassNames} href={href} title={title} aria-disabled={disabled} {...props}>
      {cloneElement(icon, { className: iconClassNames })}
    </a>
  ) : (
    <button className={buttonClassNames} title={title} disabled={disabled} {...props}>
      {cloneElement(icon, { className: iconClassNames })}
    </button>
  );
};

IconButton.displayName = 'IconButton';
